import { FeatureFlagCategory } from './feature-flag.types';

/**
 * Centralized Feature Flag Definitions
 *
 * This is the SINGLE SOURCE OF TRUTH for all feature flags.
 * All flags, their metadata, types, and arrays are defined here.
 * Usage is tracked automatically via the manifest scanner.
 *
 * ========================================
 * HOW TO ADD A NEW FEATURE FLAG
 * ========================================
 * 1. Add an entry to FEATURE_FLAGS object below
 * 2. TypeScript types are automatically generated
 * 3. Run: npm run feature-flags
 *
 * ========================================
 * HOW TO USE FEATURE FLAGS IN COMPONENTS
 * ========================================
 * PREFERRED METHOD - Observable with async pipe:
 *
 * In your component.ts:
 * ```typescript
 * import { FeatureFlagService } from '../../../../services/feature-flags';
 * import { Observable } from 'rxjs';
 *
 * export class MyComponent {
 *     myFeatureFlag$: Observable<boolean>;
 *
 *     constructor(private featureFlagService: FeatureFlagService) {}
 *
 *     ngOnInit(): void {
 *         this.myFeatureFlag$ = this.featureFlagService.isEnabled$('myFeatureFlag');
 *     }
 * }
 * ```
 *
 * In your component.html:
 * ```html
 * <div *ngIf="myFeatureFlag$ | async">
 *     Feature-flagged content
 * </div>
 *
 * <button [disabled]="!(myFeatureFlag$ | async)">
 *     Feature-flagged button
 * </button>
 * ```
 *
 * IMPORTANT NAMING CONVENTION:
 * - Name the variable with the exact flag key + '$' suffix (e.g., 'myFeatureFlag$')
 * - This allows the manifest scanner to automatically track usage
 * - Example: for flag key 'bypassCommissioningStepOrder', use 'bypassCommissioningStepOrder$'
 *
 * BENEFITS:
 * - Reactive: automatically updates when flag changes
 * - No manual subscription management needed
 * - Works with OnPush change detection
 * - Cleaner, more declarative code
 * - Automatic usage tracking by manifest scanner
 *
 * Use direct subscription only when you need to perform side effects or complex logic
 * when the flag value changes.
 */

/**
 * All feature flag definitions
 * Keys must be camelCase and match the `key` property
 * Using 'as const' to preserve literal types for type extraction
 */
export const FEATURE_FLAGS = {
    // 🌗 UI Enhancements
    enableDarkMode: {
        key: 'enableDarkMode',
        label: 'Dark Mode',
        description: 'Enable dark theme option for the user interface.',
        category: FeatureFlagCategory.EXPERIMENTAL,
        defaultValue: false,
        developmentOnly: false,
        responsibleTeam: 'sit',
    },
    showAuditLogs: {
        key: 'showAuditLogs',
        label: 'Show Audit Logs',
        description: 'Controls visibility of audit logs in navigation for authorized users.',
        category: FeatureFlagCategory.ADMIN,
        defaultValue: false,
        developmentOnly: false,
        responsibleTeam: 'integrations',
    },
    forceMyKempowerMode: {
        key: 'forceMyKempowerMode',
        label: 'Force MyKempower Mode',
        description: 'Forces the application to run in MyKempower visual and functional mode.',
        category: FeatureFlagCategory.UI_FEATURES,
        defaultValue: false,
        developmentOnly: false,
        responsibleTeam: 'sit',
    },
    showBillingDashboard: {
        key: 'showBillingDashboard',
        label: 'Show Billing Dashboard',
        description:
            'Displays the billing dashboard link in the profile menu for accessing billing and subscription information.',
        category: FeatureFlagCategory.UI_FEATURES,
        defaultValue: false,
        developmentOnly: false,
        responsibleTeam: 'sit',
    },

    enableStationDynamicsSettings: {
        key: 'enableStationDynamicsSettings',
        label: 'Enable Station Dynamics Settings',
        description:
            'Adds the "Dynamics" configuration section to station settings, allowing internal users to test load management behavior (FIFO vs Democratic).',
        category: FeatureFlagCategory.EXPERIMENTAL,
        defaultValue: false,
        developmentOnly: false,
        responsibleTeam: 'depot',
    },
    enableOcppVersionSelector: {
        key: 'enableOcppVersionSelector',
        label: 'Enable OCPP Version Selector',
        description:
            'Displays the OCPP version dropdown in the station connectivity settings for internal protocol testing.',
        category: FeatureFlagCategory.EXPERIMENTAL,
        defaultValue: false,
        developmentOnly: false,
        responsibleTeam: 'integrations',
    },
    showStationNotesTile: {
        key: 'showStationNotesTile',
        label: 'Show Station Notes Tile',
        description:
            'Displays a tile for viewing and managing station notes, intended for internal testing and administrative validation.',
        category: FeatureFlagCategory.EXPERIMENTAL,
        defaultValue: false,
        developmentOnly: false,
        responsibleTeam: 'sit',
    },
    showStationAttachmentsTile: {
        key: 'showStationAttachmentsTile',
        label: 'Show Station Attachments Tile',
        description:
            'Reveals a tile for managing station attachments (e.g., logs, configuration files) during maintenance testing.',
        category: FeatureFlagCategory.EXPERIMENTAL,
        defaultValue: false,
        developmentOnly: false,
        responsibleTeam: 'sit',
    },

    // 🧰 Debugging / Developer Aids
    showFeatureFlagIndicators: {
        key: 'showFeatureFlagIndicators',
        label: 'Show Feature Flag Indicators',
        description:
            'Displays visual indicators (dotted borders and labels) around feature-flagged elements in the UI for development.',
        category: FeatureFlagCategory.DEBUGGING,
        defaultValue: false,
        developmentOnly: true,
        responsibleTeam: 'sit',
    },
    showComponentNames: {
        key: 'showComponentNames',
        label: 'Show Component Names',
        description:
            'Shows component names when pressing CTRL+SHIFT+W. Displays red labels on components for debugging layout and component hierarchy during development.',
        category: FeatureFlagCategory.DEBUGGING,
        defaultValue: false,
        developmentOnly: true,
        responsibleTeam: 'sit',
    },
    enableEvPreconditioningLogic: {
        key: 'enableEvPreconditioningLogic',
        label: 'Enable EV Preconditioning Logic',
        description:
            'Enables configuration of preconditioning logic options within the Vehicle dialog. Allows users to switch between different preconditioning strategies ("Based on required time" vs "Always if departure time is known") for testing and validation of V2ICP preconditioning behavior.',
        category: FeatureFlagCategory.EXPERIMENTAL,
        defaultValue: false,
        developmentOnly: false,
        responsibleTeam: 'depot',
    },
    enableSchedulingOptimizationTools: {
        key: 'enableSchedulingOptimizationTools',
        label: 'Enable Scheduling Optimization Tools',
        description:
            'Exposes internal development and optimization controls in the Scheduling Settings dialog, including experimental parameters for optimization algorithms, plan recalculation strategies, and scoring weight overrides. Used for testing and comparing new scheduling logic against legacy behavior.',
        category: FeatureFlagCategory.EXPERIMENTAL,
        defaultValue: false,
        developmentOnly: false,
        responsibleTeam: 'depot',
    },

    // 🧱 Internal / System Infrastructure
    showLegacyTenantAccess: {
        key: 'showLegacyTenantAccess',
        label: 'Show Legacy Tenant Access',
        description:
            'Displays legacy tenant mappings (EMSP, CPO, Maintenance) in the access view for system administrators to validate old data.',
        category: FeatureFlagCategory.ADMIN,
        defaultValue: false,
        developmentOnly: false,
        responsibleTeam: 'sit',
    },
    enableChargerCompatibilityTab: {
        key: 'enableChargerCompatibilityTab',
        label: 'Enable Charger Compatibility Tab',
        description:
            'Shows the internal charger compatibility test tab for diagnosing OCPP integrations and firmware interoperability.',
        category: FeatureFlagCategory.EXPERIMENTAL,
        defaultValue: false,
        developmentOnly: false,
        responsibleTeam: 'integrations',
    },
    enableCertificateSigning: {
        key: 'enableCertificateSigning',
        label: 'Enable Certificate Signing Tab',
        description:
            'Adds the Certificate Signing tab in the station configuration for managing and generating charger identity certificates.',
        category: FeatureFlagCategory.SECURITY,
        defaultValue: false,
        developmentOnly: false,
        responsibleTeam: 'sit',
    },

    // 🧩 Operational / Maintenance / Security
    showChargerDeviceModelTab: {
        key: 'showChargerDeviceModelTab',
        label: 'Show Charger Device Model Tab',
        description:
            'Enables the Device Model tab in the Charger dialog to display live component metrics for diagnostics and performance analysis.',
        category: FeatureFlagCategory.MAINTENANCE,
        defaultValue: false,
        developmentOnly: false,
        responsibleTeam: 'depot',
    },
    showChargerCertificatesTab: {
        key: 'showChargerCertificatesTab',
        label: 'Show Charger Certificates Tab',
        description:
            'Enables the Certificates tab in the Charger dialog to view, install, and manage charger ISO15118 and TLS certificates.',
        category: FeatureFlagCategory.SECURITY,
        defaultValue: false,
        developmentOnly: false,
        responsibleTeam: 'depot',
    },

    // 🔧 Development/Testing Features (replacing DevelopmentService)
    showPanelCustomizationForUnsupported: {
        key: 'showPanelCustomizationForUnsupported',
        label: 'Show Panel Customization for Unsupported Stations',
        description:
            'Allows panel/display customization settings to be shown even for stations that do not natively support DisplayCustomizationKempower capability. Used for testing new firmware versions.',
        category: FeatureFlagCategory.EXPERIMENTAL,
        defaultValue: false,
        developmentOnly: false,
        responsibleTeam: 'sit',
    },
    showGeneralStationSettings: {
        key: 'showGeneralStationSettings',
        label: 'Show General Station Settings',
        description:
            'Displays the general station-specific settings page that shows configuration options outside of the shared settings profile.',
        category: FeatureFlagCategory.EXPERIMENTAL,
        defaultValue: false,
        developmentOnly: false,
        responsibleTeam: 'depot',
    },
    showNetworkSettingsForUnsupported: {
        key: 'showNetworkSettingsForUnsupported',
        label: 'Show Network Settings for Unsupported Stations',
        description:
            'Allows network adapter settings to be shown even for stations that do not natively support SettingsNetwork capability. Used for testing new firmware versions.',
        category: FeatureFlagCategory.EXPERIMENTAL,
        defaultValue: false,
        developmentOnly: false,
        responsibleTeam: 'sit',
    },
    showAdvancedConnectionProfileOptions: {
        key: 'showAdvancedConnectionProfileOptions',
        label: 'Show Advanced Connection Profile Options',
        description:
            'Reveals advanced/experimental fields in the OCPI connection profile dialog for protocol debugging and integration testing.',
        category: FeatureFlagCategory.EXPERIMENTAL,
        defaultValue: false,
        developmentOnly: false,
        responsibleTeam: 'integrations',
    },
    enableSignedMeterDataWithoutData: {
        key: 'enableSignedMeterDataWithoutData',
        label: 'Enable Signed Meter Data View Without Data',
        description:
            'Allows viewing signed meter data interface even when connector has no meter data yet. Used for testing/debugging meter functionality.',
        category: FeatureFlagCategory.DEBUGGING,
        defaultValue: false,
        developmentOnly: false,
        responsibleTeam: 'sit',
    },
    enableForceRestartChargingTest: {
        key: 'enableForceRestartChargingTest',
        label: 'Enable Force Restart Charging Test',
        description:
            'Shows "Force start test" button in commissioning charging test step, allowing restart of tests even when one is already in progress.',
        category: FeatureFlagCategory.DEBUGGING,
        defaultValue: false,
        developmentOnly: false,
        responsibleTeam: 'sit',
    },
    bypassCommissioningStepOrder: {
        key: 'bypassCommissioningStepOrder',
        label: 'Bypass Commissioning Step Order',
        description:
            'Allows navigating to any commissioning step without completing previous steps. Used for development and testing to skip to specific steps.',
        category: FeatureFlagCategory.MAINTENANCE,
        defaultValue: false,
        developmentOnly: false,
        responsibleTeam: 'sit',
    },
    enableSuccessClassificationBreakBy: {
        key: 'enableSuccessClassificationBreakBy',
        label: 'Enable Success Classification Break-By',
        description:
            'Shows "Success classification" as a break-by option in reporting dashboard widgets. Used for analyzing transaction success/failure patterns.',
        category: FeatureFlagCategory.DEBUGGING,
        defaultValue: false,
        developmentOnly: false,
        responsibleTeam: 'integrations',
    },
    showOfflineAccessKeyDownload: {
        key: 'showOfflineAccessKeyDownload',
        label: 'Show Offline Access Key Download',
        description:
            'Enables the option to download offline access keys for stations, even for stations that do not natively support DownloadOfflineAccessKey capability.',
        category: FeatureFlagCategory.EXPERIMENTAL,
        defaultValue: false,
        developmentOnly: false,
        responsibleTeam: 'sit',
    },
    showStandaloneInventoryFeatures: {
        key: 'showStandaloneInventoryFeatures',
        label: 'Show Standalone Inventory Features',
        description: 'Displays inventory management features for standalone stations during development and testing.',
        category: FeatureFlagCategory.EXPERIMENTAL,
        defaultValue: false,
        developmentOnly: false,
        responsibleTeam: 'depot',
    },
    showTenantToolsMenu: {
        key: 'showTenantToolsMenu',
        label: 'Show Tenant Tools Menu',
        description:
            'Displays the tenant-level tools menu item for accessing system-wide maintenance and diagnostic features.',
        category: FeatureFlagCategory.EXPERIMENTAL,
        defaultValue: false,
        developmentOnly: false,
        responsibleTeam: 'sit',
    },
    bypassTariffTenancyCheck: {
        key: 'bypassTariffTenancyCheck',
        label: 'Bypass Tariff Tenancy Check',
        description:
            'Allows Kempower users to bypass tenancy restrictions when editing tariffs across organizations in non-dev environments. Used for support and troubleshooting.',
        category: FeatureFlagCategory.ADMIN,
        defaultValue: false,
        developmentOnly: false,
        responsibleTeam: 'integrations',
    },
    enableDiagnosticsSnapshot: {
        key: 'enableDiagnosticsSnapshot',
        label: 'Enable Diagnostics Snapshot Download',
        description:
            'Enables the ability to download comprehensive diagnostics snapshot data from the station tile for debugging and analysis.',
        category: FeatureFlagCategory.DEBUGGING,
        defaultValue: false,
        developmentOnly: false,
        responsibleTeam: 'depot',
    },
    enableCablingCrossCheckTool: {
        key: 'enableCablingCrossCheckTool',
        label: 'Enable Cabling Cross-Check Tool',
        description:
            'Enables the Matrix Test / Cabling Cross-Check diagnostic interface for verifying DC output wiring and satellite connections. Allows initiating single-output or full-site cabling tests, displaying measurement matrices, and viewing diagnostic summaries. Intended for engineering and service use during installation or maintenance.',
        category: FeatureFlagCategory.MAINTENANCE,
        defaultValue: false,
        developmentOnly: false,
        responsibleTeam: 'sit',
    },
    enableAiDiagnostics: {
        key: 'enableAiDiagnostics',
        label: 'Enable AI Diagnostics',
        description:
            'Enables the AI-powered diagnostic tile that analyzes station health, displays active alerts with detailed error information, and provides intelligent recommendations for troubleshooting. Features natural language explanations, firmware update suggestions, and quick actions for common issues.',
        category: FeatureFlagCategory.EXPERIMENTAL,
        defaultValue: false,
        developmentOnly: false,
        responsibleTeam: 'sit',
    },
    enableMergePowerUnits: {
        key: 'enableMergePowerUnits',
        label: 'Enable Merge Power Units',
        description:
            'Enables the merge power units functionality that allows combining multiple power units into a single configuration during commissioning and in the station configuration dialog. Used for adding connected power units to extend charger capacity.',
        category: FeatureFlagCategory.MAINTENANCE,
        defaultValue: false,
        developmentOnly: false,
        responsibleTeam: 'sit',
    },
} as const;
